// Indonesian number formatting without Intl, so the server and every browser
// produce exactly the same strings (thousands with a dot, decimals with a comma).

const MINUS = '−'

const groupThousands = (digits: string): string => digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.')

const trimZeros = (value: string): string => (value.includes(',') ? value.replace(/,?0+$/, '') : value)

export const formatNumber = (value: number, decimals = 0): string => {
    const fixed = Math.abs(value).toFixed(decimals)
    const [integer, fraction] = fixed.split('.')
    const sign = value < 0 && Number(fixed) !== 0 ? MINUS : ''

    return `${sign}${groupThousands(integer)}${fraction ? `,${fraction}` : ''}`
}

// Rp 1.840.250.000
export const formatRupiah = (value: number): string => {
    const sign = value < 0 ? MINUS : ''
    return `${sign}Rp ${formatNumber(Math.abs(Math.round(value)))}`
}

// 1,84 M · 845 jt · 12,5 jt · 549 rb
export const formatCompact = (value: number): string => {
    const abs = Math.abs(value)
    const sign = value < 0 ? MINUS : ''

    if (abs >= 1e12) return `${sign}${trimZeros(formatNumber(abs / 1e12, 2))} T`
    if (abs >= 1e9) return `${sign}${trimZeros(formatNumber(abs / 1e9, 2))} M`
    if (abs >= 1e6) return `${sign}${trimZeros(formatNumber(abs / 1e6, abs >= 1e8 ? 0 : 1))} jt`
    if (abs >= 1e3) return `${sign}${trimZeros(formatNumber(abs / 1e3, abs >= 1e5 ? 0 : 1))} rb`

    return `${sign}${formatNumber(abs)}`
}

// Rp 1,84 M
export const formatRupiahCompact = (value: number): string => {
    const sign = value < 0 ? MINUS : ''
    return `${sign}Rp ${formatCompact(Math.abs(value))}`
}

// 2,1%
export const formatPercent = (value: number, decimals = 1): string => `${trimZeros(formatNumber(value, decimals))}%`

// +8,4% · −0,4%
export const formatSignedPercent = (value: number, decimals = 1): string => {
    const rounded = Number(value.toFixed(decimals))
    const sign = rounded > 0 ? '+' : ''
    return `${sign}${formatPercent(rounded, decimals)}`
}

// +124 · −3
export const formatSignedNumber = (value: number, decimals = 0): string => {
    const rounded = Number(value.toFixed(decimals))
    const sign = rounded > 0 ? '+' : ''
    return `${sign}${formatNumber(rounded, decimals)}`
}

// +Rp 98,4 jt · −Rp 12 jt
export const formatSignedRupiahCompact = (value: number): string => {
    if (value === 0) return 'Rp 0'
    return `${value > 0 ? '+' : MINUS}Rp ${formatCompact(Math.abs(value))}`
}

export const getInitials = (name: string): string => {
    const words = name
        .replace(/^(PT|CV|dr\.|drg\.)\s+/i, '')
        .split(/\s+/)
        .filter(Boolean)

    if (words.length === 0) return '?'
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase()

    return `${words[0][0]}${words[1][0]}`.toUpperCase()
}
